import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Reveal } from '@/components/Reveal';
import { SEO, createBreadcrumbSchema } from '@/components/SEO';
import { Breadcrumb } from '@/components/Breadcrumb';
import { API } from '@/lib/data';
import { DEFAULT_PROCESS_STEPS, DEFAULT_PROCESS_TIMELINE } from '@/lib/defaults';
import { useState } from 'react';
import axios from 'axios';
import { MessageCircle, ClipboardList, FileText, TrendingUp, ArrowRight, ChevronRight } from 'lucide-react';

const processIcons = { MessageCircle, ClipboardList, FileText, TrendingUp };

export default function ProcessPage() {
  const [processSteps, setProcessSteps] = useState(DEFAULT_PROCESS_STEPS);

  useEffect(() => {
    axios.get(`${API}/process-steps`).then(r => setProcessSteps(r.data)).catch(console.error);
  }, []);
  return (
    <div data-testid="process-page">
      <SEO title="Tedavi Süreci" description="Prof. Dr. Nilüfer Yıldırım fonksiyonel tıp danışmanlık sürecimiz: Ön görüşme, detaylı değerlendirme, kişisel sağlık planı ve düzenli takip adımları." schema={createBreadcrumbSchema([{ name: 'Ana Sayfa', url: '/' }, { name: 'Tedavi Süreci' }])} />
      {/* Header */}
      <section className="bg-[#F5F3EF] py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <h1 className="font-heading text-[34px] sm:text-[34px] lg:text-[46px] font-semibold tracking-[-0.02em] mb-4">4 Adımda Sağlık Yolculuğu</h1>
          <p className="text-[22px] text-[#5F6B76] max-w-xl">Sağlık yolculuğunuzun her adımında yanınızdayız.</p>
        </div>
      </section>
      <Breadcrumb items={[{ label: 'Tedavi Süreci', href: '/surec', current: true }]} />

      {/* 4 Steps — Timeline */}
      <Reveal variant="fadeUp">
        <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
          {/* Desktop */}
          <div className="hidden lg:flex items-start justify-center gap-0">
            {processSteps.map((step, i) => {
              const IconComp = processIcons[step.icon] || MessageCircle;
              return (
                <Reveal key={step.step} variant="fadeUp" delay={i * 120}>
                  <div className="flex items-start">
                    <div className="flex flex-col items-center" style={{ width: 200 }}>
                      <motion.div
                        className="w-16 h-16 rounded-full bg-white border-2 border-[#1F6E7A]/30 flex items-center justify-center shadow-[0_4px_16px_-4px_rgba(31,110,122,0.15)]"
                        whileHover={{ scale: 1.12, borderColor: '#1F6E7A' }}
                        transition={{ type: 'spring', stiffness: 300 }}
                      >
                        <IconComp size={26} className="text-[#1F6E7A]" />
                      </motion.div>
                      <div className="text-[13px] font-semibold text-[#1F6E7A]/60 mt-3 mb-1">Adım {step.step}</div>
                      <h3 className="font-heading font-semibold text-[14px] text-center leading-snug">{step.title}</h3>
                      {step.description && (
                        <p className="text-[14px] text-[#5F6B76] text-center mt-2 leading-relaxed">{step.description}</p>
                      )}
                    </div>
                    {i < processSteps.length - 1 && (
                      <div className="flex items-center h-16 px-2">
                        <div className="w-16 h-[2px] bg-gradient-to-r from-[#1F6E7A]/30 to-[#1F6E7A]/15" />
                        <ChevronRight size={24} className="text-[#1F6E7A]/40 -ml-1" />
                      </div>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Mobile */}
          <div className="lg:hidden flex flex-col relative ml-6">
            <div className="absolute top-5 bottom-5 left-[18px] w-[2px] bg-gradient-to-b from-[#1F6E7A]/30 to-[#1F6E7A]/10" />
            {processSteps.map((step, i) => {
              const IconComp = processIcons[step.icon] || MessageCircle;
              return (
                <Reveal key={step.step} variant="fadeRight" delay={i * 100}>
                  <div className="flex items-center gap-5 py-4">
                    <div className="w-[38px] h-[38px] min-w-[38px] rounded-full bg-white border-2 border-[#1F6E7A]/30 flex items-center justify-center shadow-sm z-10">
                      <IconComp size={16} className="text-[#1F6E7A]" />
                    </div>
                    <div>
                      <span className="text-[12px] font-semibold text-[#1F6E7A]/60 block">Adım {step.step}</span>
                      <h3 className="font-heading font-semibold text-[14px] leading-snug">{step.title}</h3>
                      {step.description && (
                        <p className="text-[14px] text-[#5F6B76] mt-1 leading-relaxed">{step.description}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      </Reveal>

      {/* Treatment Journey Infographic */}
      <Reveal>
        <section className="bg-[#F5F3EF] py-16 sm:py-20">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10">
            <h2 className="font-heading text-[30px] font-semibold mb-3">Tedavi Yolculuğunuz</h2>
            <p className="text-[14px] text-[#5F6B76] mb-10">Sizinle birlikte yürüyeceğimiz sürecin genel akışı</p>

            <div className="relative">
              {/* Vertical line */}
              <div className="absolute top-3 bottom-3 left-[15px] sm:left-[19px] w-[2px] bg-gradient-to-b from-[#1F6E7A]/40 via-[#1F6E7A]/20 to-[#1F6E7A]/5" />

              {[
                {
                  label: 'Analiz ve Yol Haritası',
                  text: 'Kapsamlı sağlık geçmişi ve yaşam tarzı değerlendirmesi yapılır; ileri düzey laboratuvar testleri ve analizler planlanır. Tüm veriler bir arada analiz edilerek kök nedenler belirlenir ve kişiye özel tedavi yol haritası oluşturulur.',
                  accent: '#1F6E7A',
                  image: 'https://images.unsplash.com/photo-1758691462814-485c3672e447?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
                },
                {
                  label: 'Uygulama ve Takip',
                  text: 'Beslenme düzeni, takviyeler, gerekli durumlarda serum destekleri ve ilaç düzenlemeleri uygulamaya alınıp yakından takip edilir.',
                  accent: '#1F6E7A',
                  image: 'https://images.unsplash.com/photo-1609150990057-f13c984a12f6?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
                },
                {
                  label: 'Tedavi Değerlendirme',
                  text: 'Uygulanan tedavinin etkinliği kapsamlı biçimde değerlendirilir; kontrol testleri ve iyileşme süreci analiz edilir. Gerekli protokol güncellemeleri yapılarak tedavi planı kişiye göre revize edilir.',
                  accent: '#1F6E7A',
                  image: 'https://images.unsplash.com/photo-1579154341184-22069e4614d2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
                },
                {
                  label: 'Koruma ve Optimizasyon',
                  text: 'Elde edilen sonuçların korunması için daha seyrek aralıklarla kontrol görüşmeleri planlanır. Uzun vadeli sağlık hedefleri desteklenir ve yaşam kalitesi optimize edilir.',
                  accent: '#1F6E7A',
                  image: 'https://images.pexels.com/photos/8413190/pexels-photo-8413190.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400',
                },
              ].map((item, i) => (
                <Reveal key={i} variant="fadeRight" delay={i * 80}>
                  <div className="flex gap-4 sm:gap-5 mb-6 last:mb-0 relative">
                    {/* Dot */}
                    <div className="relative z-10 mt-1.5">
                      <div className="w-[32px] h-[32px] sm:w-[40px] sm:h-[40px] rounded-full border-2 flex items-center justify-center bg-white shadow-sm" style={{ borderColor: item.accent + '50' }}>
                        <span className="text-[13px] sm:text-[15px] font-bold" style={{ color: item.accent }}>{i + 1}</span>
                      </div>
                    </div>
                    {/* Card with image */}
                    <motion.div
                      className="flex-1 bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden"
                      whileHover={{ y: -2, boxShadow: '0 8px 24px -8px rgba(0,0,0,0.08)' }}
                      transition={{ duration: 0.25 }}
                    >
                      <div className="flex flex-col sm:flex-row">
                        <div className="flex-1 p-4 sm:p-5">
                          <h3 className="font-heading font-semibold text-[14px] sm:text-[14px] mb-1.5" style={{ color: item.accent }}>{item.label}</h3>
                          <p className="text-[15px] sm:text-[14px] text-[#5F6B76] leading-relaxed">{item.text}</p>
                        </div>
                        <div className="sm:w-[160px] md:w-[200px] shrink-0">
                          <img src={item.image} alt={item.label} className="w-full h-40 sm:h-full object-cover" loading="lazy" />
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      </Reveal>

      {/* CTA */}
      <Reveal>
        <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 text-center">
          <h2 className="font-heading text-[30px] font-semibold mb-4">İlk Adımı Atın</h2>
          <p className="text-[14px] text-[#5F6B76] mb-6 max-w-md mx-auto">Sağlık yolculuğunuza başlamak için ön görüşme talep edin.</p>
          <Link
            to="/iletisim"
            className="inline-flex items-center px-6 py-3 rounded-full bg-[#1F6E7A] text-white text-sm font-semibold shadow-[0_8px_24px_-12px_rgba(31,110,122,0.4)] hover:bg-[#185960] hover:-translate-y-0.5 transition-all"
          >
            Ön Görüşme Talep Et
          </Link>
        </section>
      </Reveal>
    </div>
  );
}
